import { zodResolver } from '@hookform/resolvers/zod';
import { type ApiResult, isApiError } from '@saay/api-client';
import {
  formatLocalDate,
  MOBILE_E164_PATTERN,
  normalizeDigits,
  normalizeMobileField,
  numberedPlanLabel,
  statusLabel,
  toPersianDigits,
} from '@saay/formatting';
import {
  applyFieldErrors,
  Banner,
  Button,
  Checkbox,
  ChoiceCards,
  CommandFeedback,
  Dialog,
  FieldErrorList,
  NativeSelect,
  NumberField,
  SegmentedButtons,
  TextArea,
  TextField,
  useToast,
} from '../kit.js';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import type {
  AdminMerchant,
  InstallmentPlanSetReplace,
  InstallmentPlanSetReplaced,
  MerchantBranch,
  MerchantBranchCreate,
  MerchantRole,
  MerchantUserCreate,
  MerchantUserCreated,
} from '../api.js';
import { type CommandOutcome, useCommand } from '@saay/web-app';
import { AdminSolarDateField } from '../components/admin-date-field.js';
import { useSubmitBlocked } from '../components/form.js';
import { MobileField } from '../components/masked-fields.js';
import { GeographyFields } from '../components/pickers.js';
import { setupKeys } from '../queries.js';
import { tehranToday } from '../validation.js';
import { FormReasonChips } from '../components/reason-field.js';

const text = (max: number) =>
  z.string().trim().min(1, 'این مورد الزامی است.').max(max, `حداکثر ${max} کاراکتر مجاز است.`);

function useInvalidateMerchant(merchantId: string) {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: setupKeys.merchant(merchantId) }),
      queryClient.invalidateQueries({ queryKey: setupKeys.merchants }),
    ]);
}

// ---------------------------------------------------------------------------------------------
// Add branch (createMerchantBranch, AC §17.5.2)

const branchSchema = z.object({
  addressFa: text(500),
  cityId: z.string().min(1, 'شهر را انتخاب کنید.'),
  nameFa: text(100),
  provinceId: z.string().min(1, 'استان را انتخاب کنید.'),
});
type BranchValues = z.infer<typeof branchSchema>;
const BRANCH_FIELDS = ['nameFa', 'provinceId', 'cityId', 'addressFa'];

export function AddBranchDialog({
  merchant,
  onOpenChange,
  open,
}: {
  readonly merchant: AdminMerchant;
  readonly onOpenChange: (open: boolean) => void;
  readonly open: boolean;
}) {
  const toast = useToast();
  const invalidate = useInvalidateMerchant(merchant.id);
  const offline = useSubmitBlocked();
  const [unmapped, setUnmapped] = useState<ReturnType<typeof applyFieldErrors>>([]);
  const form = useForm<BranchValues>({
    defaultValues: {
      addressFa: '',
      cityId: '',
      nameFa: '',
      provinceId: merchant.province.id,
    },
    resolver: zodResolver(branchSchema),
  });
  const command = useCommand<MerchantBranchCreate, MerchantBranch>(
    'merchant.branch.create',
    (client, body, intent) =>
      client.post(`/admin/merchants/${merchant.id}/branches`, { body, intent }),
    {
      onSuccess: async (result: ApiResult<MerchantBranch>) => {
        await invalidate();
        toast.success({ confirmedBy: result, title: `شعبه «${result.data.nameFa}» ثبت شد` });
        form.reset();
        onOpenChange(false);
      },
    },
  );

  function handle(outcome: CommandOutcome<MerchantBranch>) {
    if (outcome.status !== 'error' || !isApiError(outcome.error)) return;
    const error = outcome.error;
    setUnmapped(applyFieldErrors(error, BRANCH_FIELDS, form.setError));
    if (error.code === 'GEOGRAPHY_MISMATCH') {
      form.setError('cityId', { message: error.messageFa, type: 'server' });
    }
  }

  async function submit(values: BranchValues) {
    setUnmapped([]);
    handle(await command.submit(values));
  }

  return (
    <Dialog
      onOpenChange={(next) => {
        if (!next) command.reset();
        onOpenChange(next);
      }}
      open={open}
      title="افزودن شعبه"
    >
      <form className="admin-form" noValidate onSubmit={form.handleSubmit(submit)}>
        <TextField
          error={form.formState.errors.nameFa?.message}
          label="نام شعبه"
          {...form.register('nameFa')}
        />
        <Controller
          control={form.control}
          name="provinceId"
          render={({ field: province }) => (
            <Controller
              control={form.control}
              name="cityId"
              render={({ field: city }) => (
                <GeographyFields
                  cityError={form.formState.errors.cityId?.message}
                  cityId={city.value}
                  idPrefix="new-branch"
                  onCityChange={city.onChange}
                  onProvinceChange={province.onChange}
                  provinceError={form.formState.errors.provinceId?.message}
                  provinceId={province.value}
                />
              )}
            />
          )}
        />
        <TextArea
          error={form.formState.errors.addressFa?.message}
          label="آدرس شعبه"
          {...form.register('addressFa')}
        />
        <FieldErrorList errors={unmapped} />
        <CommandFeedback
          onCheckStatus={() => void command.checkStatus().then(handle)}
          state={command.state}
        />
        <Button
          disabled={offline.blocked}
          loading={command.state.status === 'pending'}
          type="submit"
        >
          ثبت شعبه
        </Button>
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------------
// Add merchant user (createAdminMerchantUser, AC §17.5.2, D-29, D-30)

export const MERCHANT_ROLES: readonly MerchantRole[] = [
  'MERCHANT_ADMIN',
  'FINANCE_MANAGER',
  'SELLER',
  'COURIER',
];

/** One short line per role under its name (same wording as the merchant panel). */
const ROLE_HINTS: Record<MerchantRole, string> = {
  COURIER: 'فروش حضوری، بدون مرجوعی',
  FINANCE_MANAGER: 'تسویه، گزارش مالی و فروش‌ها',
  MERCHANT_ADMIN: 'همهٔ بخش‌های فروشگاه',
  SELLER: 'فروش و مرجوعی',
};

export const isBranchRole = (role: string) => role === 'SELLER' || role === 'COURIER';

export function RoleChoiceField({
  disabled,
  error,
  onChange,
  value,
}: {
  readonly disabled?: boolean;
  readonly error?: string | undefined;
  readonly onChange: (role: MerchantRole) => void;
  readonly value: MerchantRole;
}) {
  return (
    <ChoiceCards
      {...(disabled === undefined ? {} : { disabled })}
      {...(error === undefined ? {} : { error })}
      legend="نقش"
      name="new-merchant-user-role"
      onChange={(next) => onChange(next as MerchantRole)}
      options={MERCHANT_ROLES.map((roleKey) => ({
        description: ROLE_HINTS[roleKey],
        label: statusLabel('fixedRole', roleKey),
        value: roleKey,
      }))}
      value={value}
    />
  );
}

const userSchema = z
  .object({
    branchIds: z.array(z.string()),
    firstNameFa: text(100),
    lastNameFa: text(100),
    mobileE164: z
      .string()
      .transform(normalizeMobileField)
      .refine(
        (value) => MOBILE_E164_PATTERN.test(value),
        'شماره موبایل را مانند ۰۹۱۲۱۲۳۴۵۶۷ وارد کنید.',
      ),
    role: z.enum(['MERCHANT_ADMIN', 'FINANCE_MANAGER', 'SELLER', 'COURIER']),
  })
  .superRefine((value, context) => {
    if (isBranchRole(value.role) && value.branchIds.length === 0) {
      context.addIssue({
        code: 'custom',
        message: 'برای این نقش دست‌کم یک شعبه انتخاب کنید.',
        path: ['branchIds'],
      });
    }
    if (value.branchIds.length > 50) {
      context.addIssue({ code: 'custom', message: 'حداکثر ۵۰ شعبه.', path: ['branchIds'] });
    }
  });
type UserInput = z.input<typeof userSchema>;
type UserValues = z.output<typeof userSchema>;
const USER_FIELDS = ['mobileE164', 'firstNameFa', 'lastNameFa', 'role', 'branchIds'];

/** The request body: merchant-wide roles send `branchIds: []` (AC §17.5.2). */
export function toMerchantUserCreate(values: UserValues): MerchantUserCreate {
  return {
    branchIds: isBranchRole(values.role) ? values.branchIds : [],
    firstNameFa: values.firstNameFa,
    lastNameFa: values.lastNameFa,
    mobileE164: values.mobileE164,
    role: values.role,
  };
}

export function AddUserDialog({
  activeBranches,
  hasMerchantAdmin,
  merchant,
  onOpenChange,
  open,
}: {
  readonly activeBranches: readonly MerchantBranch[];
  readonly hasMerchantAdmin: boolean;
  readonly merchant: AdminMerchant;
  readonly onOpenChange: (open: boolean) => void;
  readonly open: boolean;
}) {
  const toast = useToast();
  const invalidate = useInvalidateMerchant(merchant.id);
  const offline = useSubmitBlocked();
  const [unmapped, setUnmapped] = useState<ReturnType<typeof applyFieldErrors>>([]);
  const form = useForm<UserInput, unknown, UserValues>({
    defaultValues: {
      branchIds: [],
      firstNameFa: '',
      lastNameFa: '',
      mobileE164: '',
      role: hasMerchantAdmin ? 'SELLER' : 'MERCHANT_ADMIN',
    },
    resolver: zodResolver(userSchema),
  });
  const role = form.watch('role');
  const command = useCommand<MerchantUserCreate, MerchantUserCreated>(
    'merchant.user.create',
    (client, body, intent) =>
      client.post(`/admin/merchants/${merchant.id}/users`, { body, intent }),
    {
      onSuccess: async (result) => {
        await invalidate();
        toast.success({
          confirmedBy: result,
          description: 'دعوت با پیامک ارسال می‌شود؛ با اولین ورود، کاربر فعال می‌شود.',
          title: 'کاربر فروشگاه دعوت شد',
        });
        form.reset();
        onOpenChange(false);
      },
    },
  );

  function handle(outcome: CommandOutcome<MerchantUserCreated>) {
    if (outcome.status !== 'error' || !isApiError(outcome.error)) return;
    const error = outcome.error;
    const rest = applyFieldErrors(error, USER_FIELDS, form.setError);
    const branchErrors = rest.filter((item) => item.field.startsWith('branchIds'));
    if (branchErrors[0] !== undefined) {
      form.setError('branchIds', { message: branchErrors[0].messageFa, type: 'server' });
    }
    if (error.code === 'MERCHANT_USER_ASSIGNMENT_CONFLICT') {
      form.setError('mobileE164', { message: error.messageFa, type: 'server' });
    }
    setUnmapped(rest.filter((item) => !item.field.startsWith('branchIds')));
  }

  async function submit(values: UserValues) {
    setUnmapped([]);
    handle(await command.submit(toMerchantUserCreate(values)));
  }

  const errors = form.formState.errors;

  return (
    <Dialog
      onOpenChange={(next) => {
        if (!next) command.reset();
        onOpenChange(next);
      }}
      open={open}
      title="افزودن کاربر فروشگاه"
    >
      <form className="admin-form" noValidate onSubmit={form.handleSubmit(submit)}>
        {hasMerchantAdmin ? null : (
          <Banner tone="warning">
            این فروشگاه هنوز مدیر ندارد؛ اولین کاربر معمولاً مدیر فروشگاه است.
          </Banner>
        )}
        <Controller
          control={form.control}
          name="role"
          render={({ field, fieldState }) => (
            <RoleChoiceField
              error={fieldState.error?.message}
              onChange={field.onChange}
              value={field.value}
            />
          )}
        />
        <div className="admin-grid-2">
          <TextField
            error={errors.firstNameFa?.message}
            label="نام"
            {...form.register('firstNameFa')}
          />
          <TextField
            error={errors.lastNameFa?.message}
            label="نام خانوادگی"
            {...form.register('lastNameFa')}
          />
        </div>
        <Controller
          control={form.control}
          name="mobileE164"
          render={({ field, fieldState }) => (
            <MobileField
              error={fieldState.error?.message}
              label="شماره موبایل"
              onBlur={field.onBlur}
              onChange={field.onChange}
              value={field.value}
            />
          )}
        />
        {isBranchRole(role) ? (
          <fieldset aria-describedby="new-user-branches-error" className="admin-fieldset">
            <legend>شعبه‌های مجاز</legend>
            {activeBranches.length === 0 ? (
              <p className="admin-muted">شعبه فعالی وجود ندارد؛ اول یک شعبه اضافه کنید.</p>
            ) : (
              <Controller
                control={form.control}
                name="branchIds"
                render={({ field }) => (
                  <div className="admin-checkbox-list">
                    {activeBranches.map((branch) => (
                      <Checkbox
                        checked={field.value.includes(branch.id)}
                        key={branch.id}
                        label={branch.nameFa}
                        onChange={(event) =>
                          field.onChange(
                            event.target.checked
                              ? [...field.value, branch.id]
                              : field.value.filter((id) => id !== branch.id),
                          )
                        }
                      />
                    ))}
                  </div>
                )}
              />
            )}
            {errors.branchIds?.message === undefined ? null : (
              <span className="saay-field__error" id="new-user-branches-error" role="alert">
                {errors.branchIds.message}
              </span>
            )}
          </fieldset>
        ) : (
          <p className="admin-muted">این نقش به همه شعبه‌های فروشگاه دسترسی دارد.</p>
        )}
        <FieldErrorList errors={unmapped} />
        <CommandFeedback
          onCheckStatus={() => void command.checkStatus().then(handle)}
          state={command.state}
        />
        <div className="admin-actions admin-actions--end">
          <Button
            disabled={offline.blocked}
            loading={command.state.status === 'pending'}
            type="submit"
          >
            دعوت کاربر
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------------
// Replace the one plan (replaceInstallmentPlanActivations, AC §17.5.4, §19.3, §23.3; D-62, D-79).
// `plans` stays an array: one installment plan and, independently, an optional wallet-cash plan.
// A NUMBERED plan carries its count and interval (D-79); the other types carry neither.

const INSTALLMENT_COUNTS = [1, 2, 3, 4, 5, 6] as const;

const PLAN_SEGMENT_OPTIONS = [
  { key: 'END_OF_SALE_MONTH', label: 'آخر ماه' },
  ...INSTALLMENT_COUNTS.map((count) => ({
    key: `NUMBERED:${count}`,
    label: `${count.toLocaleString('fa-IR')} قسطی`,
  })),
] as const;

function planSegmentValue(type: string, installmentCount: string): string {
  if (type === 'END_OF_SALE_MONTH') return 'END_OF_SALE_MONTH';
  if (type === 'NUMBERED' && installmentCount !== '') return `NUMBERED:${installmentCount}`;
  return '';
}

/** The type and count of a segment key («آخر ماه», «۳ قسطی»); `undefined` for an unknown key. */
function planFromSegment(key: string): { count: string; type: string } | undefined {
  if (key === 'END_OF_SALE_MONTH') return { count: '', type: 'END_OF_SALE_MONTH' };
  const match = /^NUMBERED:(\d+)$/.exec(key);
  return match === null ? undefined : { count: match[1] ?? '', type: 'NUMBERED' };
}

/**
 * The merchant's one plan as segments (D-62): «آخر ماه» or «۱ قسطی» to «۶ قسطی». The merchant
 * create wizard and the plan dialog use the same control, so both read the same.
 */
export function PlanChoiceField({
  count,
  error,
  onChange,
  optional = false,
  type,
}: {
  readonly count: string;
  readonly error?: string | undefined;
  readonly onChange: (type: string, count: string) => void;
  readonly optional?: boolean;
  readonly type: string;
}) {
  return (
    <>
      <SegmentedButtons
        label="طرح اقساط"
        onChange={(key) => {
          if (key === 'NO_INSTALLMENT') {
            onChange('', '');
            return;
          }
          const plan = planFromSegment(String(key));
          if (plan !== undefined) onChange(plan.type, plan.count);
        }}
        options={[
          ...PLAN_SEGMENT_OPTIONS,
          ...(optional ? [{ key: 'NO_INSTALLMENT', label: 'بدون اقساط' }] : []),
        ]}
        value={planSegmentValue(type, count) || (optional ? 'NO_INSTALLMENT' : undefined)}
      />
      {error === undefined ? null : (
        <span className="saay-field__error" role="alert">
          {error}
        </span>
      )}
    </>
  );
}

/** The interval of a NUMBERED plan in days, with the allowed range under it (D-79). */
export function PlanIntervalField({
  bounds,
  error,
  id,
  name,
  onBlur,
  onChange,
  value,
}: {
  readonly bounds: PlanIntervalBounds | undefined;
  readonly error?: string | undefined;
  readonly id?: string;
  readonly name?: string;
  readonly onBlur?: () => void;
  readonly onChange: (value: string) => void;
  readonly value: string;
}) {
  return (
    <NumberField
      error={error}
      hint={bounds === undefined ? 'تعداد روز میان دو قسط.' : intervalBoundsHint(bounds)}
      {...(id === undefined ? {} : { id })}
      label="فاصلهٔ اقساط (روز)"
      max={bounds?.maxIntervalDays ?? INTERVAL_SCHEMA_MAX}
      min={bounds?.minIntervalDays ?? 1}
      {...(name === undefined ? {} : { name })}
      {...(onBlur === undefined ? {} : { onBlur })}
      onChange={(next) => onChange(next === null ? '' : String(next))}
      suffix="روز"
      value={parseDays(value)}
    />
  );
}

/**
 * A plan as admins read it: «۴ ماهه ۴ قسط» for NUMBERED (D-111), «۴ قسط» when the interval is
 * missing, and the central label otherwise («پرداخت آخر ماه»; an unknown type neutral).
 */
export function planLabel(type: string, count: number | null, intervalDays: number | null): string {
  if (type === 'WALLET_CASH') return 'پرداخت نقدی';
  if (type !== 'NUMBERED' || count === null) return statusLabel('installmentPlanType', type);
  return numberedPlanLabel(count, intervalDays);
}

/** The interval bounds a NUMBERED plan must fit (`AdminMerchant.effectiveBounds`, AC §17.3.3). */
export interface PlanIntervalBounds {
  readonly maxIntervalDays: number;
  readonly maxTotalDurationDays: number;
  readonly minIntervalDays: number;
}

/** The contract's schema range for `plans[].intervalDays` (AC §23.3); bounds narrow it further. */
const INTERVAL_SCHEMA_MAX = 3660;

function parseDays(text: string): number | null {
  const normalized = normalizeDigits(text).trim();
  return /^\d{1,6}$/.test(normalized) ? Number(normalized) : null;
}

/** «مجاز: ۷ تا ۶۰ روز؛ تعداد × فاصله حداکثر ۱۸۰ روز» (the effective bounds, AC §23.3 step 6). */
export function intervalBoundsHint(bounds: PlanIntervalBounds): string {
  return `مجاز: ${toPersianDigits(bounds.minIntervalDays)} تا ${toPersianDigits(bounds.maxIntervalDays)} روز؛ تعداد اقساط × فاصله حداکثر ${toPersianDigits(bounds.maxTotalDurationDays)} روز`;
}

/** The plan form schema; with `bounds`, the interval is checked against them before sending. */
export function plansSchema(bounds?: PlanIntervalBounds) {
  return z
    .object({
      effectiveFrom: z.string().min(1, 'تاریخ شروع را وارد کنید.'),
      installmentCount: z.string(),
      intervalDays: z.string(),
      reason: text(500),
      type: z.string(),
      walletCash: z.boolean(),
    })
    .superRefine((values, context) => {
      // D-114: the store may offer installments, wallet cash, or both, but not neither.
      if (values.type !== 'NUMBERED' && values.type !== 'END_OF_SALE_MONTH') {
        if (!values.walletCash) {
          context.addIssue({
            code: 'custom',
            message: 'یک طرح اقساط یا پرداخت نقدی انتخاب کنید.',
            path: ['type'],
          });
        }
        return;
      }
      if (values.type !== 'NUMBERED') return;
      const count = Number(values.installmentCount);
      const countValid = Number.isInteger(count) && count >= 1 && count <= 6;
      if (!countValid) {
        context.addIssue({
          code: 'custom',
          message: 'تعداد اقساط را انتخاب کنید.',
          path: ['installmentCount'],
        });
      }
      const interval = parseDays(values.intervalDays);
      const issue = (message: string) =>
        context.addIssue({ code: 'custom', message, path: ['intervalDays'] });
      if (values.intervalDays.trim() === '') {
        issue('فاصلهٔ اقساط را به روز وارد کنید.');
      } else if (interval === null || interval < 1 || interval > INTERVAL_SCHEMA_MAX) {
        issue(
          `فاصلهٔ اقساط باید عدد صحیح بین ۱ تا ${toPersianDigits(INTERVAL_SCHEMA_MAX)} روز باشد.`,
        );
      } else if (bounds !== undefined) {
        if (interval < bounds.minIntervalDays || interval > bounds.maxIntervalDays) {
          issue(
            `فاصلهٔ اقساط باید بین ${toPersianDigits(bounds.minIntervalDays)} تا ${toPersianDigits(bounds.maxIntervalDays)} روز باشد.`,
          );
        } else if (countValid && count * interval > bounds.maxTotalDurationDays) {
          issue(
            `مدت کل اقساط (${toPersianDigits(count)} × ${toPersianDigits(interval)} روز) نباید از ${toPersianDigits(bounds.maxTotalDurationDays)} روز بیشتر باشد.`,
          );
        }
      }
    });
}
export type PlansValues = z.infer<ReturnType<typeof plansSchema>>;

export function toPlanSetReplace(
  values: PlansValues,
  expectedVersion: number,
): InstallmentPlanSetReplace {
  const plans: InstallmentPlanSetReplace['plans'] = [];
  if (values.type === 'NUMBERED') {
    plans.push({
      installmentCount: Number(values.installmentCount),
      intervalDays: parseDays(values.intervalDays) ?? 0,
      type: 'NUMBERED',
    });
  } else if (values.type === 'END_OF_SALE_MONTH') {
    plans.push({ type: 'END_OF_SALE_MONTH' });
  }
  if (values.walletCash) plans.push({ type: 'WALLET_CASH' });
  return {
    effectiveFrom: values.effectiveFrom,
    expectedVersion,
    plans,
    reason: values.reason,
  };
}

function planDefaults(merchant: AdminMerchant): PlansValues {
  const plan = merchant.activePlanSet.find((item) => item.type !== 'WALLET_CASH');
  return {
    effectiveFrom: tehranToday(),
    installmentCount: plan?.type === 'NUMBERED' ? String(plan.installmentCount ?? '') : '',
    // D-79: the current interval, or the migration default (30 days) for a first NUMBERED plan.
    intervalDays:
      plan?.type === 'NUMBERED' && plan.intervalDays !== null ? String(plan.intervalDays) : '30',
    reason: '',
    type: plan?.type === 'NUMBERED' || plan?.type === 'END_OF_SALE_MONTH' ? plan.type : '',
    walletCash: merchant.activePlanSet.some((item) => item.type === 'WALLET_CASH'),
  };
}

/** The bounds codes of AC §23.3 step 6 belong to the interval field (D-79). */
const PLAN_INTERVAL_CODES: ReadonlySet<string> = new Set([
  'INSTALLMENT_INTERVAL_OUT_OF_BOUNDS',
  'INVALID_TOTAL_DURATION',
]);

function planErrorField(
  field: string,
): 'effectiveFrom' | 'installmentCount' | 'intervalDays' | 'reason' | 'type' | undefined {
  if (field === 'effectiveFrom' || field === 'reason') return field;
  if (field.includes('installmentCount')) return 'installmentCount';
  if (field.includes('intervalDays')) return 'intervalDays';
  if (field === 'plans' || field.startsWith('plans')) return 'type';
  return undefined;
}

export function ReplacePlansDialog({
  merchant,
  onOpenChange,
  open,
}: {
  readonly merchant: AdminMerchant;
  readonly onOpenChange: (open: boolean) => void;
  readonly open: boolean;
}) {
  const toast = useToast();
  const invalidate = useInvalidateMerchant(merchant.id);
  const offline = useSubmitBlocked();
  const bounds = merchant.effectiveBounds;
  const form = useForm<PlansValues>({
    defaultValues: planDefaults(merchant),
    resolver: zodResolver(plansSchema(bounds)),
  });
  const planType = form.watch('type');
  const walletCash = form.watch('walletCash');
  const command = useCommand<InstallmentPlanSetReplace, InstallmentPlanSetReplaced>(
    'merchant.plans.replace',
    (client, body, intent) =>
      client.post(`/admin/merchants/${merchant.id}/installment-plan-activations`, { body, intent }),
    {
      onSuccess: async (result) => {
        await invalidate();
        toast.success({
          confirmedBy: result,
          description: `از ${formatLocalDate(result.data.effectiveFrom, 'long')}`,
          title: 'طرح اقساطی ثبت شد',
        });
        onOpenChange(false);
      },
    },
  );

  const error = command.state.error;
  const conflict = isApiError(error) && error.code === 'RESOURCE_VERSION_CONFLICT';
  // A bounds refusal is shown on the interval field, not again as a command error.
  const onIntervalField = isApiError(error) && PLAN_INTERVAL_CODES.has(error.code);

  function handle(outcome: CommandOutcome<InstallmentPlanSetReplaced>) {
    if (outcome.status !== 'error' || !isApiError(outcome.error)) return;
    const error = outcome.error;
    if (error.code === 'RESOURCE_VERSION_CONFLICT') {
      // Reload the merchant so the next attempt sends the current version (a new payload, a new key).
      void invalidate();
    }
    if (PLAN_INTERVAL_CODES.has(error.code)) {
      // D-79 (AC §23.3 step 6): the effective bounds may have changed; reload them as well.
      form.setError('intervalDays', { message: error.messageFa, type: 'server' });
      void invalidate();
    }
    for (const fieldError of error.fieldErrors) {
      const name = planErrorField(fieldError.field);
      if (name !== undefined) {
        form.setError(name, { message: fieldError.messageFa, type: 'server' });
      }
    }
  }

  async function submit(values: PlansValues) {
    handle(await command.submit(toPlanSetReplace(values, merchant.version)));
  }

  return (
    <Dialog
      onOpenChange={(next) => {
        if (!next) command.reset();
        onOpenChange(next);
      }}
      open={open}
      // Seven plan segments sit on one line only at the wide width (they wrapped at 32rem).
      size="wide"
      title="طرح اقساطی"
    >
      <form className="admin-form" noValidate onSubmit={form.handleSubmit(submit)}>
        <PlanChoiceField
          count={form.watch('installmentCount')}
          error={
            form.formState.errors.type?.message ?? form.formState.errors.installmentCount?.message
          }
          onChange={(type, count) => {
            form.setValue('type', type, { shouldValidate: true });
            form.setValue('installmentCount', count, { shouldValidate: true });
          }}
          optional={walletCash}
          type={planType}
        />
        <Controller
          control={form.control}
          name="walletCash"
          render={({ field }) => (
            <Checkbox
              checked={field.value}
              label="پرداخت نقدی از کیف پول"
              onChange={(event) => field.onChange(event.target.checked)}
            />
          )}
        />
        {planType === 'NUMBERED' ? (
          <Controller
            control={form.control}
            name="intervalDays"
            render={({ field, fieldState }) => (
              <PlanIntervalField
                bounds={bounds}
                error={fieldState.error?.message}
                name={field.name}
                onBlur={field.onBlur}
                onChange={field.onChange}
                value={field.value}
              />
            )}
          />
        ) : null}
        <Controller
          control={form.control}
          name="effectiveFrom"
          render={({ field, fieldState }) => (
            <AdminSolarDateField
              error={fieldState.error?.message}
              label="تاریخ شروع"
              name={field.name}
              onBlur={field.onBlur}
              onChange={field.onChange}
              value={field.value}
            />
          )}
        />
        <TextArea
          error={form.formState.errors.reason?.message}
          label="دلیل تغییر"
          {...form.register('reason')}
        />
        <FormReasonChips form={form} intent="merchant." />
        {conflict ? (
          <Banner tone="warning">
            اطلاعات فروشگاه در این فاصله تغییر کرده است. صفحه به‌روز شد؛ انتخاب‌ها را بازبینی و
            دوباره ثبت کنید.
          </Banner>
        ) : null}
        <CommandFeedback
          onCheckStatus={() => void command.checkStatus().then(handle)}
          state={conflict || onIntervalField ? { ...command.state, status: 'idle' } : command.state}
        />
        <Button
          disabled={offline.blocked}
          loading={command.state.status === 'pending'}
          type="submit"
        >
          ثبت طرح
        </Button>
      </form>
    </Dialog>
  );
}
